import type { ChromeLabelType, ChromeTranslateFn } from './chrome-label.js';
import { chromeLabelText, viewLabel } from './chrome-label.js';
import { WORK_AREA_VISIBILITIES, type WorkAreaVisibilityType } from './types/index.js';

/**
 * F-0737 (`docs/concept-ownership-organisations-and-instances.md` §10.17): the work areas the product
 * makes on its own, by seed id. One today: the Personal work area every account starts with.
 *
 * ⛔ Platform-agnostic: no translator is imported; one is handed in where the name is drawn, as
 * T-0846 does for the built-in views (`chrome-label.ts`).
 */
export const WORK_AREA_SEED_IDS = {
  PERSONAL: 'personal',
} as const;

export type WorkAreaSeedIdType = (typeof WORK_AREA_SEED_IDS)[keyof typeof WORK_AREA_SEED_IDS];

/**
 * PW-R3: the Personal work area's address and stored name. The slug is the same in every language;
 * the stored name is English, for every reader that cannot translate (the CLI, MCP, a sort order).
 */
export const PERSONAL_WORK_AREA = {
  seedId: WORK_AREA_SEED_IDS.PERSONAL,
  slug: 'personal',
  storedName: 'Personal',
} as const;

const SEED_TITLES: Readonly<Record<WorkAreaSeedIdType, 'workAreaPersonal'>> = {
  personal: 'workAreaPersonal',
};

/** A seed's name as a chrome label, translated where it is drawn; null for an id no seed has. */
export function workAreaSeedLabel(seedId: string | null | undefined): ChromeLabelType | null {
  if (!seedId || !Object.prototype.hasOwnProperty.call(SEED_TITLES, seedId)) return null;
  return viewLabel(SEED_TITLES[seedId as WorkAreaSeedIdType]);
}

/**
 * PW-R3, PW-R4: a work area's name in the reader's language. While `nameSeedId` is set the name is
 * the product's, said through `t`; once the person renamed it (`nameSeedId` null) it is theirs and
 * drawn as typed, in every language.
 */
export function workAreaNameIn(
  t: ChromeTranslateFn,
  workArea: { readonly name: string; readonly nameSeedId?: string | null },
): string {
  const label = workAreaSeedLabel(workArea.nameSeedId);
  return label ? chromeLabelText(label, t) : workArea.name;
}

/**
 * The translator a client registered for the names it draws (PW-R3), or null: the stored English
 * name then. The web registers the open page's (`web/src/i18n/pageTranslator.ts`), so every label
 * built from `workAreaDisplayName` — `workAreaQualifiedLabel` included — speaks the reader's
 * language without each of its call sites holding a translator.
 *
 * ⛔ Platform-agnostic still: nothing is imported, a client hands its translator in.
 */
let seedTranslator: ChromeTranslateFn | null = null;

export function setWorkAreaSeedTranslator(t: ChromeTranslateFn | null): void {
  seedTranslator = t;
}

/** A work area's name as a reader is shown it: {@link workAreaNameIn} with the registered translator. */
export function workAreaDisplayName(workArea: {
  readonly name: string;
  readonly nameSeedId?: string | null;
}): string {
  if (!seedTranslator) return workArea.name;
  const said = workAreaNameIn(seedTranslator, workArea);
  // A translator with no catalogue yet answers the key itself: the stored name is the better word.
  return said.startsWith('chromeTitles.') ? workArea.name : said;
}

/**
 * PW-R4: whether a name sent for a seeded work area is the seed's own word, in any language the
 * caller lists. A German reader who saves the *Persönlich* they were shown has not renamed it.
 */
export function isSeedName(
  seedId: string | null | undefined,
  name: string,
  wordsBySeed: Readonly<Record<string, readonly string[]>>,
): boolean {
  if (!seedId) return false;
  const words = wordsBySeed[seedId] ?? [];
  const sent = name.trim().toLocaleLowerCase();
  return words.some((word) => word.toLocaleLowerCase() === sent);
}

/**
 * PW-R5: what a new Personal work area may start as. Public is never one: a default that publishes a
 * person's own work to strangers is not a default anybody chooses.
 */
export const PERSONAL_WORK_AREA_VISIBILITIES = [
  WORK_AREA_VISIBILITIES.PRIVATE,
  WORK_AREA_VISIBILITIES.ORGANISATION,
] as const satisfies readonly WorkAreaVisibilityType[];

export type PersonalWorkAreaVisibilityType = (typeof PERSONAL_WORK_AREA_VISIBILITIES)[number];

/** PW-R5, PW-16: the setting as a System Admin reads it (`GET /system-settings/personal-work-area`). */
export interface IPersonalWorkAreaSettingView {
  /** What a new Personal work area starts as now. */
  readonly visibility: PersonalWorkAreaVisibilityType;
  /** What it starts as while nothing is stored, on this deployment's offering. */
  readonly defaultVisibility: PersonalWorkAreaVisibilityType;
  /** Whether a System Admin stored a choice; false means the default applies. */
  readonly stored: boolean;
  /** A stored value this build could not read; the default applies, and saving again fixes it. */
  readonly unreadable: boolean;
}
